import { useDemoSender } from "@/api";
import { userAtom } from "@/atoms/userAtom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { DayOfWeek } from "@/generatedApi/unifiedApi";
import { useLineConnection } from "@/hooks/useLineConnection";
import { setUserProperties, trackToggleLineDelivery } from "@/services/analytics";
import { useAtomValue } from "jotai";
import { AlertCircle, Bell } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

const isWeekend = (day: DayOfWeek): boolean =>
  day === DayOfWeek.SATURDAY || day === DayOfWeek.SUNDAY;

const updateLineDeliveryUserProperties = (data: { enabled: boolean; dayOfWeek: DayOfWeek }) => {
  setUserProperties({
    line_delivery_enabled: data.enabled,
    line_delivery_day_of_week: data.dayOfWeek,
    weekend_delivery_enabled: data.enabled && isWeekend(data.dayOfWeek)
  });
};

type LineDeliveryFormData = {
  enabled: boolean;
  dayOfWeek: DayOfWeek;
  time: string;
};

type LineDeliverySettingsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const DAYS: { label: string; value: DayOfWeek }[] = [
  { label: "月", value: DayOfWeek.MONDAY },
  { label: "火", value: DayOfWeek.TUESDAY },
  { label: "水", value: DayOfWeek.WEDNESDAY },
  { label: "木", value: DayOfWeek.THURSDAY },
  { label: "金", value: DayOfWeek.FRIDAY },
  { label: "土", value: DayOfWeek.SATURDAY },
  { label: "日", value: DayOfWeek.SUNDAY }
];

const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, "0");
  return { label: `${hour}:00`, value: `${hour}:00` };
});

export const LineDeliverySettingsModal = ({
  open,
  onOpenChange
}: LineDeliverySettingsModalProps) => {
  const user = useAtomValue(userAtom);
  const {
    isLinked,
    isDeliverySuspended,
    startLineConnection,
    unlinkLineConnection,
    isLoadingLinkStatus,
    isLinkProcessing
  } = useLineConnection();
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [configExists, setConfigExists] = useState(true); // 設定の存在フラグ

  // ユーザー設定取得用
  const {
    sender: getUserConfig,
    isLoading: isLoadingGet,
    error: errorGet
  } = useDemoSender({
    apiName: "user_config",
    serviceName: "userConfig",
    operationId: "getUserConfig"
  });

  // ユーザー設定作成用
  const {
    sender: createUserConfig,
    isLoading: isLoadingCreate,
    error: errorCreate
  } = useDemoSender({
    apiName: "user_config",
    serviceName: "userConfig",
    operationId: "createUserConfig"
  });

  // ユーザー設定更新用
  const {
    sender: updateUserConfig,
    isLoading: isLoadingUpdate,
    error: errorUpdate
  } = useDemoSender({
    apiName: "user_config",
    serviceName: "userConfig",
    operationId: "updateUserConfig"
  });

  const { watch, setValue, handleSubmit, reset } = useForm<LineDeliveryFormData>({
    defaultValues: {
      enabled: true,
      dayOfWeek: DayOfWeek.SATURDAY,
      time: "09:00"
    }
  });

  const enabled = watch("enabled");
  const dayOfWeek = watch("dayOfWeek");
  const time = watch("time");

  // 親モーダルが閉じられたら確認ダイアログをリセット
  useEffect(() => {
    if (!open) {
      setShowUnlinkConfirm(false);
    }
  }, [open]);

  // モーダルが開いたときにユーザー設定を取得
  useEffect(() => {
    if (open && isLinked && user?.userId) {
      const userId = user.userId;
      const fetchUserConfig = async () => {
        try {
          const config = await getUserConfig({ userId });
          if (config) {
            setConfigExists(true);
            reset({
              enabled: config.line_delivery_enabled ?? true,
              dayOfWeek: config.line_delivery_day_of_week ?? DayOfWeek.SATURDAY,
              time: config.line_delivery_time ?? "09:00"
            });
            updateLineDeliveryUserProperties({
              enabled: config.line_delivery_enabled ?? true,
              dayOfWeek: config.line_delivery_day_of_week ?? DayOfWeek.SATURDAY
            });
          } else {
            // configがundefinedの場合は設定が存在しない（404エラー）
            setConfigExists(false);
            // デフォルト値はフォームの初期値として既に設定されているのでresetは不要
          }
        } catch (error: any) {
          // エラーが発生した場合
          console.error("ユーザー設定の取得に失敗:", error);
          setConfigExists(false);
        }
      };
      fetchUserConfig();
    }
  }, [open, isLinked, user?.userId, getUserConfig, reset]);

  // エラーハンドリング
  useEffect(() => {
    if (errorGet) {
      toast.error("設定の読み込みに失敗しました");
    }
  }, [errorGet]);

  const onSubmit = async (data: LineDeliveryFormData) => {
    if (!user?.userId) {
      toast.error("ユーザー情報が取得できません");
      return;
    }

    try {
      let result;
      if (configExists) {
        // 既存の設定を更新
        result = await updateUserConfig({
          userId: user.userId,
          requestBody: {
            line_delivery_enabled: data.enabled,
            line_delivery_day_of_week: data.dayOfWeek,
            line_delivery_time: data.time
          }
        });
      } else {
        // 新規に設定を作成
        result = await createUserConfig({
          requestBody: {
            user_id: user.userId,
            line_delivery_enabled: data.enabled,
            line_delivery_day_of_week: data.dayOfWeek,
            line_delivery_time: data.time
          }
        });
      }

      // レスポンスを確認して成功したかチェック
      if (result) {
        if (!configExists) {
          setConfigExists(true); // 作成成功後は存在するようになる
        }
        toast.success("設定を保存しました");
        // GA4 イベント送信と user property 更新
        if (user?.userId) {
          trackToggleLineDelivery({
            user_id: user.userId,
            enabled: data.enabled,
            day_of_week: data.dayOfWeek,
            time: data.time
          });
          updateLineDeliveryUserProperties({
            enabled: data.enabled,
            dayOfWeek: data.dayOfWeek
          });
        }
        // モーダルは閉じない（ユーザーが手動で閉じるまで表示）
      } else {
        // resultがundefinedの場合はエラー
        console.error("設定の保存に失敗: レスポンスがundefined");
      }
    } catch (error) {
      console.error("設定の保存に失敗:", error);
    }
  };

  // 保存時のエラーハンドリング
  useEffect(() => {
    if (errorCreate || errorUpdate) {
      toast.error("設定の保存に失敗しました");
    }
  }, [errorCreate, errorUpdate]);

  const handleLineLink = async () => {
    try {
      await startLineConnection();
    } catch (error) {
      console.error("LINE連携の開始に失敗:", error);
      toast.error("LINE連携の開始に失敗しました");
    }
  };

  const handleLineUnlink = () => {
    setShowUnlinkConfirm(true);
  };

  const confirmUnlink = async () => {
    try {
      await unlinkLineConnection();
      toast.success("LINE連携を解除しました");
      setShowUnlinkConfirm(false);
      onOpenChange(false);
    } catch (error) {
      console.error("LINE連携の解除に失敗:", error);
      toast.error("LINE連携の解除に失敗しました");
    }
  };

  // LINE未連携時の表示
  if (!isLinked) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-80 rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-orange-500" />
              スポット配信設定
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-gray-600">
              毎週あなたにピッタリな新しいスポットや
              <br />
              保存して行けてないスポットをLINEでお届け✨
            </p>
            <Button
              className="w-full bg-[#06C755] text-white hover:bg-[#06C755]/90"
              onClick={handleLineLink}
              disabled={isLoadingLinkStatus || isLinkProcessing}
            >
              {isLoadingLinkStatus
                ? "読み込み中..."
                : isLinkProcessing
                ? "処理中..."
                : "LINE連携する"}
            </Button>
            <img
              src="/static/line/line-delivery-preview.png"
              alt="LINE配信イメージ"
              className="mx-auto h-80 w-auto"
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // LINE連携済み時の表示
  return (
    <>
      <Dialog open={open && !showUnlinkConfirm} onOpenChange={onOpenChange}>
        <DialogContent className="min-w-80 max-w-96 rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-orange-500" />
              スポット配信設定
            </DialogTitle>
          </DialogHeader>
          {isLoadingGet ? (
            // 読み込み中の表示
            <div className="flex items-center justify-center py-12">
              <Spinner className="h-8 w-8" />
            </div>
          ) : (
            <div className="w-full space-y-4">
              <p className="text-xs">
                毎週あなたにピッタリな新しいスポットや
                <br />
                保存して行けてないスポットをLINEでお届け✨
                <br />
                ※本日配信済みの場合は、翌日以降に配信されます
              </p>

              {/* 配信停止警告エリア */}
              {isDeliverySuspended && (
                <div className="space-y-3 rounded-sm bg-yellow-100 p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-500" />
                    <div className="flex-1">
                      <p className="text-xs">
                        連携は完了していますが、LINE で受け取るには友だち登録し、
                        ブロックを解除していただく必要があります。
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    className="w-full bg-[#06C755] text-white hover:bg-[#06C755]/90"
                    onClick={() =>
                      window.open(
                        import.meta.env.VITE_LINE_FRIEND_ADD_URL,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  >
                    LINE友だち追加する
                  </Button>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  {/* 配信通知トグル */}
                  <div className="flex items-center justify-between">
                    <Label htmlFor="enabled" className="text-base font-medium">
                      スポット配信通知
                    </Label>
                    <Switch
                      id="enabled"
                      checked={enabled}
                      onCheckedChange={(checked) => setValue("enabled", checked)}
                      className="data-[state=checked]:bg-green-500"
                    />
                  </div>

                  {/* 曜日選択 */}
                  <div className="space-y-1">
                    <Label className="font-medium">曜日</Label>
                    <div className="flex w-full gap-1">
                      {DAYS.map((day) => (
                        <Button
                          key={day.value}
                          type="button"
                          variant={dayOfWeek === day.value ? "default" : "outline"}
                          className={`h-8 flex-1 rounded-full p-0 text-xs ${
                            dayOfWeek === day.value
                              ? "bg-gradient-to-br from-[#0fff78] to-[#86faff] text-white hover:opacity-90"
                              : "border-[#0fff78] text-[#0fff78] hover:bg-[#0fff78]/10"
                          }`}
                          onClick={() => setValue("dayOfWeek", day.value)}
                          disabled={!enabled}
                        >
                          {day.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* 時間選択 */}
                  <div className="space-y-1">
                    <Label className="font-medium">時間</Label>
                    <Select
                      value={time}
                      onValueChange={(value) => setValue("time", value)}
                      disabled={!enabled}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* 確定ボタン */}
                <div className="flex flex-col items-center gap-2">
                  <Button
                    type="submit"
                    size="lg"
                    className="w-1/2"
                    disabled={
                      isLoadingGet || isLoadingCreate || isLoadingUpdate || isLinkProcessing
                    }
                  >
                    {isLoadingGet
                      ? "読み込み中..."
                      : isLoadingCreate || isLoadingUpdate
                      ? "保存中..."
                      : "確定"}
                  </Button>

                  {/* LINE連携解除リンク */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="lg"
                    className="w-1/2 text-sm"
                    onClick={handleLineUnlink}
                    disabled={isLoadingCreate || isLoadingUpdate || isLinkProcessing}
                  >
                    LINE連携解除
                  </Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* LINE連携解除確認ダイアログ */}
      <Dialog open={showUnlinkConfirm} onOpenChange={setShowUnlinkConfirm}>
        <DialogContent className="w-80 rounded-lg">
          <DialogHeader>
            <DialogTitle>LINE連携を解除しますか？</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              LINE連携を解除すると、
              <br />
              スポット配信通知が届かなくなります。
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowUnlinkConfirm(false)}
              >
                キャンセル
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={confirmUnlink}
                disabled={isLoadingCreate || isLoadingUpdate || isLinkProcessing}
              >
                {isLoadingCreate || isLoadingUpdate || isLinkProcessing ? "処理中..." : "解除する"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
